import { useTranslation } from 'react-i18next';
import PromptDialog from 'components/dialogs/PromptDialog';
import UploadFileDialog from 'components/dialogs/UploadFileDialog';
import ConfirmDialog from 'components/dialogs/ConfirmDialog';
import useModal from 'hooks/useModal';
import { VoyageModel } from 'models/VoyageModel';
import { CompanyModel } from 'models/CompanyModel';
import { CarModel } from 'models/StockModel';
import { exportCertificateRequired } from 'modules/cars/utils/common';
import VoyageDialog from 'modules/voyages/dialogs/VoyageDialog';
import UploadEDResult from 'modules/voyages/dialogs/UploadEDResult';
import JPInvoiceSeparatorDialog from 'modules/voyages/dialogs/JPInvoiceSeparatorDialog';
import DeliveryFaxPreviewDialog from 'modules/voyages/dialogs/DeliveryFaxPreviewDialog';
import JpInvoicePackagesDialog from 'modules/voyages/dialogs/JpInvoicePackagesDialog';
import PriceTablesDialog from 'modules/voyages/dialogs/PriceTablesDialog';
import TransferCarsDialog from 'modules/voyages/dialogs/TransferCarsDialog';
import Api from 'utils/api';


const useVoyageModals = () => {
    const {t} = useTranslation();
    const {showModal, hideModal} = useModal();

    const openAddVoyageModal = () => showModal(VoyageDialog,
        {create: true},
        {
            title: t('add_title', {value: t('voyage')}),
            maxWidth: 'md'
        })
        .then(voyage => Api.voyages.create(voyage))
        .then(hideModal);

    const openEditVoyageModal = (voyage: VoyageModel) => showModal(VoyageDialog,
        {voyage},
        {
            title: t('edit_title', {value: t('voyage')}),
            maxWidth: 'md'
        })
        .then(voyage => Api.voyages.update(voyage))
        .then(hideModal);

    const openCancelVoyageModal = (id: number) => showModal(PromptDialog,
        {required: true},
        {
            title: t('reason_of_voyage_cancellation'),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(reason => Api.voyages.cancel(id, reason))
        .then(hideModal);

    const openLostVoyageModal = (id: number) => showModal(PromptDialog,
        {required: true},
        {
            title: t('reason_of_vessel_loss'),
            maxWidth: 'sm',
            fullWidth: true,
        })
        .then(reason => Api.voyages.lost(id, reason))
        .then(hideModal);

    const openUploadEDModal = (voyage: VoyageModel) => showModal(UploadFileDialog,
        {
            title: t('upload_ed_description'),
            accept: {
                'application/zip': ['.zip'],
            }
        },
        {
            title: t('upload_ed_for', {value: voyage.number}),
            maxWidth: 'xs',
        })
        .then(files => Api.voyages.uploadED(voyage.id, files[0]))
        .then(res => {
            showModal(UploadEDResult,
                res.data,
                {title: t('result'),},
                true,
            );

            return Promise.resolve();
        });

    const openJPInvoiceSplitterModal = (voyage: VoyageModel, company: CompanyModel, cars: CarModel[]) => Api.voyages
        .ed(voyage.id, company.id)
        .then(res => showModal(JPInvoiceSeparatorDialog,
            {
                urls: res.data.urls,
                cars,
            },
            {
                title: t('separate_jp_invoices_for', {company: company.name, voyage: voyage.number}),
                maxWidth: 'xl',
                fullWidth: true,
            }))
        .then(Api.voyages.setJpInvoicePos)
        .then(hideModal);

    const openVoyageNoteModal = (voyage: VoyageModel) => showModal(PromptDialog,
        {
            value: voyage.note,
            rows: 20,
        },
        {
            title: t('note'),
            maxWidth: 'md',
            fullWidth: true,
        })
        .then(note => Api.voyages.putNote(voyage.id, note))
        .then(hideModal);

    const openPriceTablesModal = (portId: number) => showModal(PriceTablesDialog,
        {portId},
        {
            title: t('price_tables'),
            maxWidth: 'lg',
        })
        .then(Api.voyages.setPriceTables)
        .then(hideModal);

    const confirmResetInvoices = (voyage: VoyageModel) => showModal(ConfirmDialog,
        {},
        {
            title: t('confirm_reset_invoices'),
            fullScreen: false,
        })
        .then(() => Api.voyages.resetInvoices(voyage.id))
        .finally(hideModal);

    const openToyamaInvoicePackagesModal = (voyageId: number) => showModal(JpInvoicePackagesDialog,
        {
            voyageId,
            toyama: true,
            isReady: car => Boolean(car.tallyNumber && (!exportCertificateRequired(car) || car.exportCertificate?.originalArrivedAt))
        },
        {
            title: t('jp_invoice_packages'),
            maxWidth: 'xl',
            fullWidth: true,
        })
        .then(hideModal);

    const openJpInvoicePackagesModal = (voyageId: number) => showModal(JpInvoicePackagesDialog,
        {
            voyageId,
            toyama: false,
            isReady: car => Boolean(car.yard?.arrived && (!exportCertificateRequired(car) || car.documents.exportCertificate))
        },
        {
            title: t('jp_invoice_packages'),
            maxWidth: 'xl',
            fullWidth: true,
        })
        .then(hideModal);

    const openTransferCarsDialog = (voyage: VoyageModel, cars: CarModel[]) => showModal(TransferCarsDialog,
        {cars},
        {
            title: t('load_cars'),
        })
        .then(cars => Api.voyages.transferCars(voyage.id, cars))
        .finally(hideModal);

    const loadVoyageWithConfirmation = (id: number) => Api.voyages.loaded(id)
        .catch(() => {
            showModal(ConfirmDialog,
                {},
                {
                    title: t('confirm_force_loading'),
                    fullScreen: false,
                })
                .then(() => Api.voyages.loaded(id, true))
                .finally(hideModal)
            }
        );

    const openDeliveryFaxModal = (voyage: VoyageModel, company: CompanyModel, type: string) => showModal(DeliveryFaxPreviewDialog,
            {
                voyage,
                company,
                type,
            },
            {
                title: t('delivery_fax_preview'),
                maxWidth: 'lg',
                fullWidth: true,
            })
        .then(message => Api.voyages.deliveryFaxSend(voyage.id, company.id, type, message))
        .then(hideModal);

    return {
        openAddVoyageModal,
        openEditVoyageModal,
        openCancelVoyageModal,
        openLostVoyageModal,
        openUploadEDModal,
        openJPInvoiceSplitterModal,
        openVoyageNoteModal,
        openPriceTablesModal,
        confirmResetInvoices,
        openToyamaInvoicePackagesModal,
        openJpInvoicePackagesModal,
        openTransferCarsDialog,
        loadVoyageWithConfirmation,
        openDeliveryFaxModal,
    };
};

export default useVoyageModals;
